import { useEffect, useRef, useState } from "react";
import GuestService from "../../../services/guest.service";
import { hasPendingSchlageAccess, schlageAccessState } from "../../../common/schlageAccessState";

// Socket notifications trigger a scoped re-read, never client-side activation.
// Background confirmation must not reset unsaved reservation edits.
export default function useSchlageAccessRefresh({
  bookingId,
  hotel,
  passcodes,
  setFormValues,
}) {
  const [refreshError, setRefreshError] = useState(false);
  const passcodesRef = useRef(passcodes);
  passcodesRef.current = passcodes;
  const pending = hasPendingSchlageAccess(passcodes, hotel);
  const supportsAsyncAccess = hotel?.lockType === "schlage" ||
    passcodes?.some(code => schlageAccessState(code, hotel) !== null);
  useEffect(() => {
    setRefreshError(false);
    if (!bookingId || !hotel?._id || !supportsAsyncAccess)
      return undefined;
    let active = true;
    let timer;
    let fetching = false;
    let queued = false;
    const refresh = async () => {
      if (fetching) {
        queued = true;
        return;
      }
      fetching = true;
      clearTimeout(timer);
      try {
        const { data } = await GuestService.getBooking(bookingId);
        if (!active) return;
        setFormValues((previous) =>
          String(previous._id) === String(bookingId)
            ? {
                ...previous,
                passcodes: data.passcodes || [],
                ekeys: data.ekeys || [],
              }
            : previous,
        );
        setRefreshError(!!data.accessRefreshError);
      } catch {
        if (active) setRefreshError(true);
      } finally {
        fetching = false;
        if (active && queued) {
          queued = false;
          refresh();
        } else if (active && pending) timer = setTimeout(refresh, 10000);
      }
    };
    const changed = ({ detail }) => {
      if (String(detail?.hotelId) !== String(hotel._id)) return;
      if (
        !detail?.refreshAll &&
        !detail?.bookingIds?.some((id) => String(id) === String(bookingId)) &&
        !(
          detail?.passcodeId &&
          passcodesRef.current?.some(
            (code) =>
              code._id && String(code._id) === String(detail.passcodeId),
          )
        )
      )
        return;
      refresh();
    };
    window.addEventListener("loxe:schlage-passcode", changed);
    if (pending) timer = setTimeout(refresh, 10000);
    return () => {
      active = false;
      clearTimeout(timer);
      window.removeEventListener("loxe:schlage-passcode", changed);
    };
  }, [bookingId, hotel?._id, supportsAsyncAccess, pending, setFormValues]);
  return { pending, refreshError };
}
